import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { Clock, Users, CheckCircle, AlertCircle, MessageSquare, BarChart3, FileText, Plus, Send, ThumbsUp, ThumbsDown, User, Settings, LogOut, ChevronRight, ChevronDown, ChevronUp, X, Mic, MicOff, Mail, Search, RefreshCw, Trash2, XCircle, ArrowLeft, Loader2, Bell, BellOff, Eye, Download, Filter, Columns, ArrowUpDown, Maximize2, Minimize2, ChevronLeft, ShieldAlert, Heart, Phone } from 'lucide-react';
import { useToast, useCrisisAlert, ToastProvider, CrisisContext } from '../shared.jsx';
import { storage, addAuditEntry, calculateWorkingHours, OWL_LOGO, EMAIL_CONFIG, detectHarmContent, notifyMentorOfHarmDetection, sendNotification, VoiceInputButton, useVoiceInput, initFCM, removeFCMToken, notificationPermissionGranted, requestNotificationPermission, CrisisResourceModal, getUrlParams, clearUrlParams, initFirebase } from '../shared.jsx';

function AgreeImplementStep({ conflict, user, canEdit, onUpdate, onReturnToOverview }) {
  const toast = useToast();
  const [hasAcknowledged, setHasAcknowledged] = useState(false);

  // Get the selected solution from Evaluate & Select
  const evaluateSelect = conflict.steps?.evaluateSelect;
  const selectedId = evaluateSelect?.selected;
  const allScores = evaluateSelect?.scores;

  // Get the early resolution if it exists
  const earlyResolution = conflict.steps?.identifyDefine?.resolutionCheck?.canResolve 
    ? conflict.steps.identifyDefine.resolutionCheck 
    : null;

  // Get all alternatives
  const alternatives = conflict.steps?.exploreAlternatives?.alternatives || [];
  
  // Combine to find the selected solution
  const allOptions = [];
  if (earlyResolution?.resolution) {
    allOptions.push({
      id: 'early-resolution',
      text: earlyResolution.resolution,
      createdBy: earlyResolution.proposedBy,
      createdByName: earlyResolution.proposedByEmail || earlyResolution.proposedByName,
      timestamp: earlyResolution.timestamp,
      isEarlyResolution: true
    });
  }
  allOptions.push(...alternatives);

  const selectedSolution = allOptions.find(opt => opt.id === selectedId);

  // Check if user has already acknowledged
  const agreeImplement = conflict.steps?.agreeImplement;
  const userAcknowledgment = agreeImplement?.acknowledged?.find(a => a.userId === user.id);

  React.useEffect(() => {
    if (userAcknowledgment) {
      setHasAcknowledged(true);
    }
  }, [userAcknowledgment]);

  const handleAcknowledge = async () => {
    try {
      const updated = {
        ...conflict,
        steps: {
          ...conflict.steps,
          agreeImplement: {
            ...conflict.steps.agreeImplement,
            acknowledged: [
              ...(conflict.steps.agreeImplement?.acknowledged || []),
              {
                userId: user.id,
                userName: user.name,
                userEmail: user.email,
                timestamp: new Date().toISOString()
              }
            ]
          }
        },
        updatedAt: new Date().toISOString(),
        updatedBy: user.id
      };

      // Check if all mentees have acknowledged
      const allAcknowledged = updated.steps.agreeImplement.acknowledged;
      const totalMentees = conflict.mentees?.length || 1;

      if (allAcknowledged.length >= totalMentees) {
        // All have acknowledged - set the resolution and mark as complete
        updated.actualOutcome = selectedSolution?.text || 'Resolution agreed upon';
        updated.steps.agreeImplement.completed = true;
        updated.status = 'follow-up';
        updated.statusHistory.push({
          status: 'follow-up',
          timestamp: new Date().toISOString()
        });
      }

      addAuditEntry(updated, user.id, user.name, 'Acknowledged resolution', 'Agree & Implement phase', user.email);
      await storage.set(`conflict:${conflict.id}`, updated, true);
      setHasAcknowledged(true);
      await onUpdate();
      toast('You have acknowledged the resolution!');
      // Only call onReturnToOverview for non-last acknowledgers (no action left → overview).
      // When status transitions to follow-up, let ConflictProcess auto-navigate instead.
      if (onReturnToOverview && allAcknowledged.length < totalMentees) onReturnToOverview();
    } catch (error) {
      console.error('Error acknowledging:', error);
      toast('Failed to acknowledge. Please try again.');
    }
  };

  const allAcknowledgments = agreeImplement?.acknowledged || [];
  const allHaveAcknowledged = agreeImplement?.completed;

  return (
    <div className="space-y-6">
      <div>
        <h4 className="font-medium text-stone-800 mb-3">Agree & Implement</h4>
        <p className="text-sm text-stone-600 mb-4">
          Review and acknowledge the selected solution. Once all parties acknowledge, this becomes the official resolution.
        </p>
      </div>

      {/* Selected Solution Display */}
      {selectedSolution ? (
        <div>
          <div className="p-6 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border-2 border-green-200 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center">
                <CheckCircle className="w-7 h-7 text-white" />
              </div>
              <div>
                <h5 className="font-medium text-green-800 text-xl">Selected Resolution</h5>
                <p className="text-sm text-green-700">
                  Chosen by consensus through voting (Average: {allScores?.[selectedId]?.toFixed(1)}/5.0)
                </p>
              </div>
            </div>
            
            <div className="bg-white rounded-lg p-5 border border-green-300">
              <div className="flex items-start gap-3">
                <div className="flex-1">
                  {selectedSolution.isEarlyResolution && (
                    <div className="bg-amber-100 text-amber-700 px-3 py-1 rounded text-xs inline-block mb-3 font-medium">
                      Early Resolution Proposal
                    </div>
                  )}
                  <p className="text-stone-800 text-lg mb-3">{selectedSolution.text}</p>
                  <div className="text-sm text-stone-500">
                    Originally proposed by {selectedSolution.createdByName} · {new Date(selectedSolution.timestamp).toLocaleString()}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Acknowledgment Status */}
          {allHaveAcknowledged ? (
            <div className="p-6 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg border-2 border-blue-200">
              <div className="flex items-center gap-3 mb-3">
                <CheckCircle className="w-6 h-6 text-blue-600" />
                <h5 className="font-medium text-blue-800 text-lg">Resolution Finalized</h5>
              </div>
              <p className="text-blue-700 mb-4">
                All parties have acknowledged this resolution. This is now the official outcome of the conflict.
              </p>
              <div className="bg-white rounded-lg p-4 border border-blue-200">
                <div className="text-sm font-medium text-blue-800 mb-2">Official Resolution:</div>
                <p className="text-stone-700 italic">"{conflict.actualOutcome}"</p>
              </div>
            </div>
          ) : (
            <div>
              {/* Acknowledgment Action */}
              {!hasAcknowledged && canEdit ? (
                <div className="bg-white rounded-lg p-6 border-2 border-amber-200">
                  <div className="bg-amber-50 rounded-lg p-4 mb-4">
                    <p className="text-amber-800 font-medium mb-1">Your Acknowledgment Required</p>
                    <p className="text-amber-700 text-sm">
                      By acknowledging, you agree that this resolution accurately reflects the agreed-upon solution and commit to implementing it.
                    </p>
                  </div>
                  <button
                    onClick={handleAcknowledge}
                    className="w-full bg-green-700 hover:bg-green-800 text-white py-4 rounded-lg font-medium transition-all transform hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2"
                  >
                    <CheckCircle className="w-5 h-5" />
                    I Acknowledge This Resolution
                  </button>
                </div>
              ) : (
                <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
                  <div className="flex items-center gap-2 text-blue-800 font-medium mb-2">
                    <CheckCircle className="w-5 h-5" />
                    You have acknowledged this resolution
                  </div>
                  <p className="text-blue-600 text-sm">
                    Waiting for other participants to acknowledge...
                  </p>
                </div>
              )}

              {/* Show who has acknowledged */}
              {allAcknowledgments.length > 0 && (
                <div className="mt-6 p-4 bg-stone-50 rounded-lg border border-stone-200">
                  <div className="text-sm font-medium text-stone-700 mb-3">
                    Acknowledgments ({allAcknowledgments.length} of {conflict.mentees?.length || 1})
                  </div>
                  <div className="space-y-2">
                    {allAcknowledgments.map((ack, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm">
                        <CheckCircle className="w-4 h-4 text-green-700" />
                        <span className="text-green-700 font-medium">
                          {ack.userEmail || ack.userName}
                        </span>
                        <span className="text-stone-500 text-xs">
                          · {new Date(ack.timestamp).toLocaleTimeString()}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-12 text-stone-500">
          <AlertCircle className="w-12 h-12 mx-auto mb-3 text-stone-300" />
          <p>No solution has been selected yet</p>
        </div>
      )}
    </div>
  );
}

// Evaluate & Select Step

export default AgreeImplementStep;
